(function () {
  var initializedForms = new WeakSet();

  class ProductOptions {
    constructor(container, config) {
      this.container = container;
      this.config = config;
      this.locale = this.getLocale();
      this.groupsContainer = container.querySelector('.product-options__groups');
      this.basePrice = 0;
      this.currentUnitPrice = 0;
      this.moneyFormatter = new Intl.NumberFormat(this.locale || 'en', {
        style: 'currency',
        currency: this.getCurrency(),
      });
    }

    init() {
      if (!this.config || !Array.isArray(this.config.groups) || !this.groupsContainer) return;

      this.basePrice = this.getBasePrice();
      this.config.groups.forEach((group) => this.renderGroup(group));
      this.setupConditionalLogic();
      this.groupsContainer.addEventListener('change', () => {
        this.syncSelectedChoices();
        this.evaluateConditions();
        this.calculatePrice();
      });
      this.groupsContainer.addEventListener('input', () => this.calculatePrice());
      var quantityInput = this.getForm()?.querySelector('input[name="quantity"]');
      if (quantityInput) {
        quantityInput.addEventListener('change', () => this.updateAddToCartTotal());
        quantityInput.addEventListener('input', () => this.updateAddToCartTotal());
      }
      this.syncSelectedChoices();
      this.calculatePrice();
      this.interceptAddToCart();
      var form = this.getForm();
      if (form) {
        form.addEventListener('product:variant-change', (event) => {
          this.basePrice = Number(event.detail?.price) || 0;
          this.container.dataset.basePrice = String(this.basePrice);
          this.calculatePrice();
        });
      }
    }

    getBasePrice() {
      var dataPrice = parseFloat(this.container.dataset.basePrice);
      if (!Number.isNaN(dataPrice)) return dataPrice;

      var priceEl = document.querySelector('.price-item--regular, .product-info p');
      if (!priceEl) return 0;

      var text = priceEl.textContent.replace(/[^0-9.,]/g, '').replace(/,/g, '');
      return parseFloat(text) || 0;
    }

    renderGroup(group) {
      var wrapper = document.createElement('div');
      wrapper.className = 'product-options__group';
      wrapper.dataset.groupId = group.id || '';

      if (group.condition) {
        wrapper.dataset.conditionOption = group.condition.option_id || '';
        wrapper.dataset.conditionValue = group.condition.value || '';
        wrapper.hidden = true;
      }

      (group.options || []).forEach((option) => {
        var optionEl = this.renderOption(option, group.id || 'group');
        if (optionEl) wrapper.appendChild(optionEl);
      });

      this.groupsContainer.appendChild(wrapper);
    }

    renderOption(option, groupId) {
      switch (option.type) {
        case 'dropdown':
          return this.renderDropdown(option, groupId);
        case 'text':
          return this.renderText(option);
        case 'textarea':
          return this.renderTextarea(option);
        case 'upload':
          return this.renderUpload(option, 'upload');
        case 'attachment':
          return this.renderUpload(option, 'attachment');
        default:
          return null;
      }
    }

    renderDropdown(option, groupId) {
      var fieldset = document.createElement('fieldset');
      fieldset.className = 'po-fieldset';
      fieldset.dataset.optionId = option.id || '';
      fieldset.dataset.optionType = 'dropdown';

      var legend = this.createLegend(option);
      fieldset.appendChild(legend);

      var choicesContainer = document.createElement('div');
      choicesContainer.className = 'po-choices';

      (option.choices || []).forEach((choice, index) => {
        var label = document.createElement('label');
        label.className = 'po-choice';

        var input = document.createElement('input');
        input.type = 'radio';
        input.name = 'po-' + groupId + '-' + option.id;
        input.value = choice.value || '';
        input.dataset.price = Number(choice.price || 0);
        input.required = Boolean(option.required);
        if (Number(choice.price || 0) > 0) {
          input.dataset.priceLabel = '+' + this.formatMoney(Number(choice.price || 0));
        }
        if (option.required && index === 0) input.checked = true;
        label.appendChild(input);

        if (choice.swatch) {
          var swatch = document.createElement('span');
          swatch.className = 'po-choice__swatch';
          swatch.style.backgroundColor = choice.swatch;
          label.appendChild(swatch);
        }

        var labelText = document.createElement('span');
        labelText.className = 'po-choice__label';
        labelText.textContent = this.getLabel(choice.labels, choice.value);
        label.appendChild(labelText);

        if (Number(choice.price || 0) > 0) {
          var price = document.createElement('span');
          price.className = 'po-choice__price';
          price.textContent = '+' + this.formatMoney(Number(choice.price || 0));
          label.appendChild(price);
        }

        choicesContainer.appendChild(label);
      });

      fieldset.appendChild(choicesContainer);
      return fieldset;
    }

    renderText(option) {
      var wrapper = document.createElement('div');
      wrapper.className = 'po-field po-field--text';
      wrapper.dataset.optionId = option.id || '';
      wrapper.dataset.optionType = 'text';
      wrapper.appendChild(this.createLegend(option));

      var input = document.createElement('input');
      input.type = 'text';
      input.className = 'po-input';
      input.required = Boolean(option.required);
      if (option.max_length) input.maxLength = option.max_length;
      if (option.placeholder) input.placeholder = this.getLabel(option.placeholder);
      if (option.default != null && option.default !== '') input.value = String(option.default);
      if (option.pricing) input.dataset.pricing = JSON.stringify(option.pricing);
      wrapper.appendChild(input);

      if (option.max_length) wrapper.appendChild(this.createCounter(input, option.max_length));
      return wrapper;
    }

    renderTextarea(option) {
      var wrapper = document.createElement('div');
      wrapper.className = 'po-field po-field--textarea';
      wrapper.dataset.optionId = option.id || '';
      wrapper.dataset.optionType = 'textarea';
      wrapper.appendChild(this.createLegend(option));

      var textarea = document.createElement('textarea');
      textarea.className = 'po-textarea';
      textarea.required = Boolean(option.required);
      if (option.max_length) textarea.maxLength = option.max_length;
      if (option.placeholder) textarea.placeholder = this.getLabel(option.placeholder);
      if (option.pricing) textarea.dataset.pricing = JSON.stringify(option.pricing);
      wrapper.appendChild(textarea);

      if (option.max_length) wrapper.appendChild(this.createCounter(textarea, option.max_length));
      return wrapper;
    }

    renderUpload(option, type) {
      var wrapper = document.createElement('div');
      wrapper.className = 'po-field po-field--' + type;
      wrapper.dataset.optionId = option.id || '';
      wrapper.dataset.optionType = type;
      wrapper.appendChild(this.createLegend(option));

      var uploadArea = document.createElement('div');
      uploadArea.className = 'po-upload-area';

      var fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.className = 'po-upload-input';
      fileInput.accept = option.accept || (type === 'attachment' ? '.pdf,.doc,.docx,.zip' : 'image/*');
      fileInput.required = Boolean(option.required);
      fileInput.dataset.maxSizeMb = option.max_size_mb || (type === 'attachment' ? 10 : 5);
      if (option.pricing) fileInput.dataset.pricing = JSON.stringify(option.pricing);

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'po-upload-btn';
      button.textContent = 'Choose File';
      button.addEventListener('click', () => fileInput.click());

      var hint = document.createElement('span');
      hint.className = 'po-upload-hint';
      hint.textContent = 'Max ' + fileInput.dataset.maxSizeMb + 'MB';

      uploadArea.appendChild(fileInput);
      uploadArea.appendChild(button);
      uploadArea.appendChild(hint);
      wrapper.appendChild(uploadArea);

      var preview = document.createElement('div');
      preview.className = 'po-upload-preview';
      preview.hidden = true;

      var image = document.createElement('img');
      image.className = 'po-upload-preview__img';
      image.alt = '';

      var fileName = document.createElement('span');
      fileName.className = 'po-upload-filename';

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'po-upload-remove';
      remove.setAttribute('aria-label', 'Remove file');
      remove.textContent = 'x';
      remove.addEventListener('click', () => {
        fileInput.value = '';
        image.removeAttribute('src');
        fileName.textContent = '';
        preview.hidden = true;
        this.calculatePrice();
      });

      preview.appendChild(image);
      preview.appendChild(fileName);
      preview.appendChild(remove);
      wrapper.appendChild(preview);

      fileInput.addEventListener('change', () => this.handleFileChange(fileInput, image, fileName, preview));
      return wrapper;
    }

    handleFileChange(fileInput, image, fileName, preview) {
      var file = fileInput.files[0];
      if (!file) return;

      var maxSizeMb = parseInt(fileInput.dataset.maxSizeMb, 10) || 5;
      if (file.size > maxSizeMb * 1024 * 1024) {
        window.alert('File too large. Max ' + maxSizeMb + 'MB.');
        fileInput.value = '';
        return;
      }

      fileName.textContent = file.name;
      image.removeAttribute('src');

      if (file.type && file.type.indexOf('image/') === 0) {
        var reader = new FileReader();
        reader.onload = (event) => {
          image.src = event.target.result;
        };
        reader.readAsDataURL(file);
      }

      preview.hidden = false;
      this.calculatePrice();
    }

    setupConditionalLogic() {
      this.evaluateConditions();
    }

    evaluateConditions() {
      var groups = this.groupsContainer.querySelectorAll('[data-condition-option]');
      groups.forEach((group) => {
        var optionId = group.dataset.conditionOption;
        var targetValue = group.dataset.conditionValue;
        var checked = this.groupsContainer.querySelector('input[name$="-' + CSS.escape(optionId) + '"]:checked');
        group.hidden = !checked || checked.value !== targetValue;
      });
    }

    calculatePrice() {
      var extraTotal = 0;

      this.visibleFields('[data-option-type="dropdown"]').forEach((fieldset) => {
        var checked = fieldset.querySelector('input[type="radio"]:checked');
        if (!checked) return;

        var price = parseFloat(checked.dataset.price) || 0;
        if (price <= 0) return;

        extraTotal += price;
      });

      this.visibleFields('[data-option-type="text"] input, [data-option-type="textarea"] textarea').forEach((input) => {
        var pricing = this.parsePricing(input.dataset.pricing);
        var value = input.value.trim();
        if (!pricing || !value) return;

        var price = this.calculateFieldPrice(pricing, value);
        if (price <= 0) return;

        extraTotal += price;
      });

      this.visibleFields('[data-option-type="upload"] input[type="file"], [data-option-type="attachment"] input[type="file"]').forEach((input) => {
        var pricing = this.parsePricing(input.dataset.pricing);
        if (!pricing || pricing.strategy !== 'per_file' || input.files.length === 0) return;

        var fileCount = pricing.first_free ? Math.max(0, input.files.length - 1) : input.files.length;
        var price = fileCount * (Number(pricing.rate) || 0);
        if (price <= 0) return;

        extraTotal += price;
      });

      this.currentUnitPrice = this.basePrice + extraTotal;
      this.updateAddToCartTotal();
      return extraTotal;
    }

    getForm() {
      return this.container.closest('form') || document.querySelector('form[action*="/cart/add"]');
    }

    updateAddToCartTotal() {
      var form = this.getForm();
      if (!form) return;

      var button = form.querySelector('.product-add-to-cart');
      if (!button || button.disabled) return;

      var quantityInput = form.querySelector('input[name="quantity"]');
      var quantity = Math.max(1, parseInt(quantityInput?.value, 10) || 1);
      var unitPrice = this.currentUnitPrice || this.basePrice;
      button.dataset.calculatedUnitPrice = String(unitPrice);
      button.textContent =
        (button.dataset.addToCartLabel || 'Add to Cart') +
        ' — ' +
        this.formatMoney(unitPrice * quantity);
    }

    collectProperties() {
      var props = {};

      this.visibleFields('[data-option-type="dropdown"]').forEach((fieldset) => {
        var checked = fieldset.querySelector('input[type="radio"]:checked');
        if (!checked) return;

        var label = this.cleanLabel(fieldset.querySelector('.po-legend'));
        var choiceLabel = checked.closest('.po-choice').querySelector('.po-choice__label').textContent.trim();
        var pricePart = checked.dataset.priceLabel ? ' (' + checked.dataset.priceLabel + ')' : '';
        props[label] = choiceLabel + pricePart;
      });

      this.visibleFields('[data-option-type="text"] input, [data-option-type="textarea"] textarea').forEach((input) => {
        var value = input.value.trim();
        if (!value) return;

        var label = this.cleanLabel(input.closest('[data-option-id]').querySelector('.po-legend'));
        props[label] = value;
      });

      this.visibleFields('[data-option-type="upload"] input[type="file"], [data-option-type="attachment"] input[type="file"]').forEach((input) => {
        if (input.files.length === 0) return;

        var label = this.cleanLabel(input.closest('[data-option-id]').querySelector('.po-legend'));
        props[label] = Array.from(input.files)
          .map((file) => file.name)
          .join(', ');
      });

      return props;
    }

    interceptAddToCart() {
      var form = this.container.closest('form') || document.querySelector('form[action*="/cart/add"]');
      if (!form || initializedForms.has(form)) return;
      initializedForms.add(form);

      form.addEventListener('submit', (event) => {
        var optionContainers = Array.from(form.querySelectorAll('.product-options'));
        if (optionContainers.length === 0) return;

        event.preventDefault();

        var formData = new FormData(form);
        optionContainers.forEach((container) => {
          if (container.productOptionsInstance) {
            var props = container.productOptionsInstance.collectProperties();
            Object.keys(props).forEach((key) => formData.append('properties[' + key + ']', String(props[key])));
          }
        });

        fetch(form.action || '/cart/add', {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' },
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.status === 422) {
              window.alert(data.description || 'Sorry, this item could not be added. Please try again.');
              return;
            }
            window.location.href = '/cart';
          })
          .catch(() => {
            window.alert('An error occurred. Please try again.');
          });
      });
    }

    createLegend(option) {
      var label = document.createElement('label');
      label.className = 'po-legend';
      label.textContent = this.getLabel(option.labels, option.id);

      if (option.required) {
        var required = document.createElement('span');
        required.className = 'po-required';
        required.textContent = ' *';
        label.appendChild(required);
      }

      if (option.pricing && option.pricing.strategy && option.pricing.strategy !== 'none') {
        var hint = document.createElement('span');
        hint.className = 'po-field__pricing-hint';
        hint.textContent = this.formatPricingHint(option.pricing);
        label.appendChild(hint);
      }

      return label;
    }

    createCounter(input, maxLength) {
      var counter = document.createElement('span');
      counter.className = 'po-field__char-count';
      counter.textContent = '0/' + maxLength;
      input.addEventListener('input', () => {
        counter.textContent = input.value.length + '/' + maxLength;
      });
      return counter;
    }

    calculateFieldPrice(pricing, value) {
      switch (pricing.strategy) {
        case 'flat':
          return Number(pricing.amount) || 0;
        case 'per_char':
          return Math.max(0, value.length - (Number(pricing.free_chars) || 0)) * (Number(pricing.rate) || 0);
        case 'tiered':
          return this.calculateTieredPrice(pricing, value.length);
        default:
          return 0;
      }
    }

    calculateTieredPrice(pricing, length) {
      if (!Array.isArray(pricing.tiers)) return 0;

      for (var i = 0; i < pricing.tiers.length; i += 1) {
        var tier = pricing.tiers[i];
        if (tier.max_chars === null || length <= tier.max_chars) return Number(tier.price) || 0;
      }

      return 0;
    }

    formatPricingHint(pricing) {
      switch (pricing.strategy) {
        case 'flat':
          return '(+' + this.formatMoney(Number(pricing.amount) || 0) + ')';
        case 'per_char':
          return (
            '(+' +
            this.formatMoney(Number(pricing.rate) || 0) +
            '/char' +
            (pricing.free_chars ? ', first ' + pricing.free_chars + ' free' : '') +
            ')'
          );
        case 'tiered':
          return '(varies by length)';
        case 'per_file':
          return (
            '(+' +
            this.formatMoney(Number(pricing.rate) || 0) +
            '/file' +
            (pricing.first_free ? ', first free' : '') +
            ')'
          );
        default:
          return '';
      }
    }

    syncSelectedChoices() {
      this.groupsContainer.querySelectorAll('.po-choice').forEach((choice) => {
        var input = choice.querySelector('input[type="radio"]');
        choice.classList.toggle('is-selected', Boolean(input && input.checked));
      });
    }

    visibleFields(selector) {
      return Array.from(this.groupsContainer.querySelectorAll(selector)).filter((element) => {
        var field = element.matches('[data-option-id]') ? element : element.closest('[data-option-id]');
        var group = element.closest('.product-options__group');
        return (!field || !field.hidden) && (!group || !group.hidden);
      });
    }

    parsePricing(value) {
      if (!value) return null;
      try {
        return JSON.parse(value);
      } catch (error) {
        return null;
      }
    }

    getLabel(labels, fallback) {
      if (!labels) return fallback || '';
      if (typeof labels === 'string') return labels;
      if (labels[this.locale]) return labels[this.locale];
      if (labels.en) return labels.en;
      if (labels['zh-CN']) return labels['zh-CN'];
      return Object.values(labels)[0] || fallback || '';
    }

    getLocale() {
      return document.documentElement.lang || 'en';
    }

    getCurrency() {
      var currency = this.container.dataset.currency || window.Shopify?.currency?.active;
      return currency || 'USD';
    }

    formatMoney(amount) {
      return this.moneyFormatter.format(Number(amount) || 0);
    }

    cleanLabel(labelEl) {
      if (!labelEl) return '';
      return Array.from(labelEl.childNodes)
        .filter((node) => node.nodeType === Node.TEXT_NODE)
        .map((node) => node.textContent)
        .join('')
        .trim();
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  function initProductOptions() {
    document.querySelectorAll('.product-options').forEach((container) => {
      if (container.productOptionsInstance) return;

      var configScript = container.querySelector('[data-product-options-config]');
      if (!configScript) return;

      try {
        var config = JSON.parse(configScript.textContent);
        if (typeof config === 'string') config = JSON.parse(config);
        var instance = new ProductOptions(container, config);
        container.productOptionsInstance = instance;
        instance.init();
      } catch (error) {
        container.hidden = true;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductOptions);
  } else {
    initProductOptions();
  }
})();
